import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomFilterSelect from '../../shared/customFilterSelect';
import BreadCrumb from '../../components/breadCrumb';
import DateRangeFilter from '../../components/dateRangeFilter';
import AdminProcessScopeFilter from '../../components/adminProcessScopeFilter';
import {
  getAllBeneficiaries,
  getAttendanceBeneficiaryCounts,
  getList
} from '../../API/apiService';
import BeneficiaryItem from '../../components/characterizationAdmin/beneficiaryItem';
import DownloadBeneficiaries from '../../components/downloadBeneficiaries';
import { useLoader } from '../../contexts/loader/loader';
import { getCurrentIsoDateInBogota } from '../../utils/dateTime';
import {
  ADMIN_SCOPE_VALUES,
  matchesBeneficiaryByAdminScope,
  normalizeAdminScope
} from '../../utils/adminScope';
import { isGroupCancelled } from '../../utils/groupStatus';
import { fetchWithAuth, getApiV1BaseUrl } from '../../utils/secureMedia';
import {
  getDateFiltersFromSearchParams,
  getRecordCreatedDate,
  isDateInRange
} from '../../utils/dateFilters';
import { ENTITY_TYPE_FILTER_OPTIONS, getEntityTypeFilterLabel, matchesEntityType } from '../../utils/entityType';
import { getMultiFilterLabel, matchesAnySelected, normalizeMultiSelection, parseMultiFilterValue, serializeMultiFilterValue } from '../../utils/multiFilter';
import './style.scss';

const ALL_FILTER_ID = '__all__';
const TEACHER_GROUP_CODE_PATTERN = /GD\d+$/;
const ATTENDED_SESSION_FILTER_OPTIONS = [
  { id: ALL_FILTER_ID, name: 'Todas las sesiones' },
  { id: '1', name: '1 sesión o más' },
  { id: '2', name: '2 sesiones o más' },
  { id: '3', name: '3 sesiones o más' },
  { id: '4', name: '4 sesiones o más' },
  { id: '5', name: '5 sesiones o más' },
  { id: '6', name: '6 sesiones o más' },
  { id: '7', name: '7 sesiones o más' },
  { id: '8', name: '8 sesiones o más' },
  { id: '9', name: '9 sesiones o más' },
  { id: '10', name: '10 sesiones o más' }
];

const defaultItemsDescription = [
  {
    name: 'Nombre',
    code: 'CDE',
    value: 1
  },
  {
    name: 'Documento',
    code: 'TDE',
    value: 2
  },
  {
    name: 'Codigo de Proceso Formativo',
    code: 'GRP',
    value: 0
  },
  {
    name: 'Grupo',
    code: 'DMZ',
    value: 3
  },
  {
    name: 'Comuna',
    code: 'BAR',
    value: 4
  },
  {
    name: 'Barrio',
    code: 'TDR',
    value: 5
  },
  {
    name: 'Accion',
    code: 'OM',
    value: 6
  }
];

const copItemsDescription = [
  {
    name: 'Nombre',
    code: 'CDE',
    value: 1
  },
  {
    name: 'Documento',
    code: 'TDE',
    value: 2
  },
  {
    name: 'Codigo de Asesoría',
    code: 'GRP',
    value: 0
  },
  {
    name: 'Comuna',
    code: 'BAR',
    value: 4
  },
  {
    name: 'Barrio',
    code: 'TDR',
    value: 5
  },
  {
    name: 'Accion',
    code: 'OM',
    value: 6
  }
];

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function sortOptionsByName(options) {
  return [...options].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'es', {
      sensitivity: 'base'
    })
  );
}

function buildUniqueCodeOptions(values) {
  const unique = new Map();

  values.forEach(value => {
    const code = String(value || '').trim();
    if (!code) return;

    unique.set(code, {
      id: code,
      name: code
    });
  });

  return sortOptionsByName(Array.from(unique.values()));
}

function buildMediatorOptions(beneficiaries) {
  const unique = new Map();

  beneficiaries.forEach(beneficiary => {
    const name = getBeneficiaryMediatorName(beneficiary);
    const id = getBeneficiaryMediatorId(beneficiary) || name;
    if (!id || !name) return;

    unique.set(id, {
      id,
      name
    });
  });

  return sortOptionsByName(Array.from(unique.values()));
}

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getBeneficiaryName(beneficiary) {
  return String(
    beneficiary?.full_name ?? beneficiary?.fullName ?? beneficiary?.name ?? ''
  ).trim();
}

function getBeneficiaryDocument(beneficiary) {
  return String(beneficiary?.document ?? '').trim();
}

function getBeneficiaryId(beneficiary) {
  return String(
    beneficiary?.id ?? beneficiary?.beneficiaryId ?? beneficiary?.beneficiary_id ?? ''
  ).trim();
}

function getBeneficiarySchoolCode(beneficiary) {
  return String(
    beneficiary?.school?.code ??
      beneficiary?.activityGroup?.activity?.name ??
      ''
  ).trim();
}

function getBeneficiarySchoolName(beneficiary) {
  return String(
    beneficiary?.school?.name ??
      beneficiary?.school?.address ??
      beneficiary?.school?.organization ??
      beneficiary?.activityGroup?.activity?.line ??
      beneficiary?.activityGroup?.activity?.name ??
      ''
  ).trim();
}

function getBeneficiaryGroupCode(beneficiary) {
  return String(
    beneficiary?.group?.code ?? beneficiary?.activityGroup?.code ?? ''
  ).trim();
}

function getBeneficiaryAdvisorName(beneficiary) {
  return String(
    beneficiary?.school?.name ??
      beneficiary?.school?.address ??
      beneficiary?.school?.code ??
      beneficiary?.activityGroup?.activity?.name ??
      ''
  ).trim();
}

function getBeneficiaryMediatorId(beneficiary) {
  return String(
    beneficiary?.group?.userId ??
      beneficiary?.group?.user_id ??
      beneficiary?.group?.user?.id ??
      beneficiary?.school?.userId ??
      beneficiary?.school?.user_id ??
      beneficiary?.school?.user?.id ??
      beneficiary?.activityGroup?.userId ??
      beneficiary?.activityGroup?.user_id ??
      beneficiary?.activityGroup?.user?.id ??
      beneficiary?.userId ??
      beneficiary?.user_id ??
      beneficiary?.user?.id ??
      ''
  ).trim();
}

function getBeneficiaryMediatorName(beneficiary) {
  return (
    getUserDisplayName(beneficiary?.group?.user) ||
    getUserDisplayName(beneficiary?.school?.user) ||
    getUserDisplayName(beneficiary?.activityGroup?.user) ||
    getUserDisplayName(beneficiary?.user)
  );
}

function getGroupTypeText(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return ageRange;

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? legacyDescription
    : '';
}

function isTeacherGroup(group) {
  const groupType = normalizeText(getGroupTypeText(group));
  if (groupType.includes('docente')) return true;

  const code = String(group?.code || '').trim().toUpperCase();
  return TEACHER_GROUP_CODE_PATTERN.test(code);
}

function shouldIncludeBeneficiaryInCharacterizationAdminView(beneficiary, scope) {
  if (isGroupCancelled(beneficiary?.group)) return false;
  if (scope !== 'pf') return true;
  return !isTeacherGroup(beneficiary?.group);
}

function getBeneficiaryMunicipalityId(beneficiary) {
  return String(
    beneficiary?.municipality_id ??
      beneficiary?.municipalityId ??
      beneficiary?.municipality?.id ??
      ''
  ).trim();
}

function getBeneficiaryNeighborhoodId(beneficiary) {
  return String(
    beneficiary?.neighborhood_id ??
      beneficiary?.neighborhoodId ??
      beneficiary?.neighborhood?.id ??
      beneficiary?.neighborhood?.name ??
      ''
  ).trim();
}

function getBeneficiaryNeighborhoodName(beneficiary) {
  return String(beneficiary?.neighborhood?.name ?? '').trim();
}

function getBeneficiaryMunicipalityName(beneficiary) {
  return String(beneficiary?.municipality?.name ?? '').trim();
}

function buildLocationOptions(items, getId, getName) {
  return sortOptionsByName(
    Array.from(
      items.reduce((optionsMap, item) => {
        const id = String(getId(item) || '').trim();
        const name = String(getName(item) || '').trim();

        if (!id || !name) {
          return optionsMap;
        }

        optionsMap.set(id, {
          id,
          name
        });

        return optionsMap;
      }, new Map()).values()
    )
  );
}

function buildAttendedSessionsByBeneficiary(countsResponse) {
  const countsByBeneficiaryId =
    countsResponse?.countsByBeneficiaryId ??
    countsResponse?.counts_by_beneficiary_id ??
    countsResponse ??
    {};

  return new Map(
    Object.entries(countsByBeneficiaryId)
      .map(([beneficiaryId, total]) => [
        String(beneficiaryId).trim(),
        Number(total)
      ])
      .filter(([beneficiaryId, total]) =>
        Boolean(beneficiaryId) && Number.isFinite(total)
      )
  );
}

function buildCharacterizationFiltersSearch({
  scope,
  searchName,
  searchDocument,
  mediatorId,
  schoolCode,
  schoolName,
  groupCode,
  advisorName,
  municipalityId,
  neighborhoodId,
  attendedSessions,
  dateFrom,
  dateTo
}) {
  const nextSearchParams = new URLSearchParams();

  if (scope) nextSearchParams.set('scope', scope);
  if (searchName) nextSearchParams.set('name', searchName);
  if (searchDocument) nextSearchParams.set('document', searchDocument);
  if (mediatorId) nextSearchParams.set('mediatorId', mediatorId);
  if (schoolCode) nextSearchParams.set('schoolCode', schoolCode);
  if (schoolName) nextSearchParams.set('schoolName', schoolName);
  if (groupCode) nextSearchParams.set('groupCode', groupCode);
  if (advisorName) nextSearchParams.set('advisorName', advisorName);
  if (municipalityId) nextSearchParams.set('municipalityId', municipalityId);
  if (neighborhoodId) nextSearchParams.set('neighborhoodId', neighborhoodId);
  if (attendedSessions) nextSearchParams.set('attendedSessions', attendedSessions);
  if (dateFrom) nextSearchParams.set('dateFrom', dateFrom);
  if (dateTo) nextSearchParams.set('dateTo', dateTo);

  return nextSearchParams.toString();
}

function downloadBlob(blob, fileName) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

function resolveDownloadFileName(contentDisposition, fallback) {
  const headerValue = String(contentDisposition || '');
  if (!headerValue) return fallback;

  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const basicMatch = headerValue.match(/filename="?([^"]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallback;
}

function CharacterizationAdmin() {
  const { show } = useLoader();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReviewerView = location.pathname.startsWith('/revisor/');
  const characterizationBasePath = isReviewerView
    ? '/revisor/caracterizaciones'
    : '/administrador/menu/caracterizacion';
  const beneficiaryDetailPath = isReviewerView
    ? '/revisor/caracterizaciones/ver-beneficiario'
    : '/administrador/menu/caracterizacion/ver_beneficiarios';
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const isCopScope = adminScope === ADMIN_SCOPE_VALUES.COP;
  const characterizationDownloadMode = isCopScope
    ? 'cop'
    : adminScope === ADMIN_SCOPE_VALUES.CS
      ? 'social_control'
      : 'default';
  const itemsDescription = isCopScope
    ? copItemsDescription
    : defaultItemsDescription;

  const [beneficiaries, setBeneficiaries] = useState([]);
  const [attendanceCounts, setAttendanceCounts] = useState(null);
  const [genders, setGenders] = useState([]);
  const [hasLoadedAttendanceCounts, setHasLoadedAttendanceCounts] = useState(false);
  const [isLoadingAttendanceCounts, setIsLoadingAttendanceCounts] = useState(false);
  const [isDownloadingConsents, setIsDownloadingConsents] = useState(false);
  const [searchName, setSearchName] = useState(() =>
    String(searchParams.get('name') || '').trim()
  );
  const [searchDocument, setSearchDocument] = useState(() =>
    String(searchParams.get('document') || '').trim()
  );
  const [selectedMediatorIds, setSelectedMediatorIds] = useState(() => parseMultiFilterValue(searchParams.get('mediatorId')));
  const [selectedSchoolCodes, setSelectedSchoolCodes] = useState(() =>
    parseMultiFilterValue(searchParams.get(isCopScope ? 'advisorName' : 'schoolCode'))
  );
  const [selectedSchoolNames, setSelectedSchoolNames] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolName'))
  );
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [selectedGroupCodes, setSelectedGroupCodes] = useState(() =>
    parseMultiFilterValue(searchParams.get('groupCode'))
  );
  const [selectedMunicipalityIds, setSelectedMunicipalityIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('municipalityId'))
  );
  const [selectedNeighborhoodIds, setSelectedNeighborhoodIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('neighborhoodId'))
  );
  const [selectedAttendedSessionCounts, setSelectedAttendedSessionCounts] = useState(() =>
    parseMultiFilterValue(searchParams.get('attendedSessions'))
  );
  const initialDateFilters = getDateFiltersFromSearchParams(searchParams);
  const [dateFrom, setDateFrom] = useState(initialDateFilters.dateFrom);
  const [dateTo, setDateTo] = useState(initialDateFilters.dateTo);

  async function loadPageData() {
    await show(true);

    try {
      const [
        beneficiariesResponse,
        gendersResponse
      ] = await Promise.all([
        getAllBeneficiaries(false, adminScope),
        getList('gender')
      ]);

      setBeneficiaries(Array.isArray(beneficiariesResponse) ? beneficiariesResponse : []);
      setGenders(Array.isArray(gendersResponse) ? gendersResponse : []);
    } finally {
      await show(false);
    }
  }

  useEffect(() => {
    loadPageData();
  }, [adminScope]);

  useEffect(() => {
    setAttendanceCounts(null);
    setHasLoadedAttendanceCounts(false);
  }, [adminScope]);

  const currentFilters = useMemo(
    () => ({
      searchName,
      searchDocument,
      selectedMediatorIds, selectedSchoolCodes, selectedSchoolNames, selectedEntityTypes,
      selectedGroupCodes, selectedMunicipalityIds, selectedNeighborhoodIds,
      selectedAttendedSessionCounts,
      dateFrom,
      dateTo
    }),
    [
      searchName,
      searchDocument,
      selectedMediatorIds, selectedSchoolCodes, selectedSchoolNames, selectedEntityTypes,
      selectedGroupCodes, selectedMunicipalityIds, selectedNeighborhoodIds,
      selectedAttendedSessionCounts,
      dateFrom,
      dateTo
    ]
  );

  const attendedSessionsByBeneficiary = useMemo(
    () => buildAttendedSessionsByBeneficiary(attendanceCounts),
    [attendanceCounts]
  );

  useEffect(() => {
    const attendedSessionThresholds = selectedAttendedSessionCounts.map(Number).filter(Boolean);
    const minimumAttendedSessions = attendedSessionThresholds.length
      ? Math.min(...attendedSessionThresholds)
      : 0;
    if (
      !minimumAttendedSessions ||
      hasLoadedAttendanceCounts
    ) {
      return undefined;
    }

    let isMounted = true;

    async function loadAttendanceCounts() {
      setIsLoadingAttendanceCounts(true);
      await show(true);

      try {
        const countsResponse = await getAttendanceBeneficiaryCounts(adminScope);

        if (isMounted) {
          setAttendanceCounts(countsResponse || {});
          setHasLoadedAttendanceCounts(true);
        }
      } finally {
        if (isMounted) {
          setIsLoadingAttendanceCounts(false);
        }
        await show(false);
      }
    }

    loadAttendanceCounts();

    return () => {
      isMounted = false;
    };
  }, [
    selectedAttendedSessionCounts,
    hasLoadedAttendanceCounts,
    adminScope,
    show
  ]);

  const scopedBeneficiaries = useMemo(
    () =>
      beneficiaries.filter(beneficiary =>
        matchesBeneficiaryByAdminScope(beneficiary, adminScope) &&
        shouldIncludeBeneficiaryInCharacterizationAdminView(
          beneficiary,
          adminScope
        )
      ),
    [beneficiaries, adminScope]
  );

  function matchesBeneficiaryFilters(beneficiary, excludedKeys = []) {
    const excluded = new Set(excludedKeys);

    const matchesName =
      excluded.has('searchName') ||
      !currentFilters.searchName ||
      normalizeText(getBeneficiaryName(beneficiary)).includes(
        normalizeText(currentFilters.searchName)
      );
    const matchesDocument =
      excluded.has('searchDocument') ||
      !currentFilters.searchDocument ||
      getBeneficiaryDocument(beneficiary).includes(
        String(currentFilters.searchDocument).trim()
      );
    const matchesMediator =
      excluded.has('selectedMediator') ||
      matchesAnySelected(currentFilters.selectedMediatorIds, getBeneficiaryMediatorId(beneficiary));
    const matchesSchoolCode =
      excluded.has('selectedSchoolCode') ||
      currentFilters.selectedSchoolCodes.length === 0 ||
      currentFilters.selectedSchoolCodes.some(code => isCopScope
        ? normalizeText(getBeneficiaryAdvisorName(beneficiary)) === normalizeText(code)
        : getBeneficiarySchoolCode(beneficiary) === code);
    const matchesSchoolName =
      isCopScope ||
      excluded.has('selectedSchoolName') ||
      matchesAnySelected(currentFilters.selectedSchoolNames, getBeneficiarySchoolName(beneficiary), normalizeText);
    const matchesGroupCode =
      isCopScope ||
      excluded.has('selectedGroupCode') ||
      matchesAnySelected(currentFilters.selectedGroupCodes, getBeneficiaryGroupCode(beneficiary));
    const matchesEntity = currentFilters.selectedEntityTypes.length === 0 || currentFilters.selectedEntityTypes.some(type => matchesEntityType(beneficiary?.school || beneficiary, type));
    const matchesMunicipality =
      excluded.has('selectedMunicipalityId') ||
      matchesAnySelected(currentFilters.selectedMunicipalityIds, getBeneficiaryMunicipalityId(beneficiary));
    const matchesNeighborhood =
      excluded.has('selectedNeighborhoodId') ||
      matchesAnySelected(currentFilters.selectedNeighborhoodIds, getBeneficiaryNeighborhoodId(beneficiary));
    const attendedSessionThresholds = currentFilters.selectedAttendedSessionCounts
      .map(Number)
      .filter(Boolean);
    const minimumAttendedSessions = attendedSessionThresholds.length
      ? Math.min(...attendedSessionThresholds)
      : 0;
    const matchesAttendedSessions =
      excluded.has('selectedAttendedSessionCount') ||
      !minimumAttendedSessions ||
      (
        hasLoadedAttendanceCounts &&
        (attendedSessionsByBeneficiary.get(getBeneficiaryId(beneficiary)) || 0) >=
          minimumAttendedSessions
      );
    const matchesDate =
      excluded.has('dateRange') ||
      isDateInRange(getRecordCreatedDate(beneficiary), {
        dateFrom: currentFilters.dateFrom,
        dateTo: currentFilters.dateTo
      });

    return (
      matchesName &&
      matchesDocument &&
      matchesMediator &&
      matchesSchoolCode &&
      matchesSchoolName &&
      matchesEntity &&
      matchesGroupCode &&
      matchesMunicipality &&
      matchesNeighborhood &&
      matchesAttendedSessions &&
      matchesDate
    );
  }

  const schoolFilterOptions = useMemo(
    () => [
      {
        id: ALL_FILTER_ID,
        name: isCopScope ? 'Todas las asesorías' : 'Todos los codigos'
      },
      ...buildUniqueCodeOptions(
        scopedBeneficiaries
          .filter(beneficiary =>
            matchesBeneficiaryFilters(beneficiary, ['selectedSchoolCode'])
          )
          .map(beneficiary =>
            isCopScope
              ? getBeneficiaryAdvisorName(beneficiary)
              : getBeneficiarySchoolCode(beneficiary)
          )
      )
    ],
    [scopedBeneficiaries, currentFilters, isCopScope, attendedSessionsByBeneficiary]
  );

  const schoolNameFilterOptions = useMemo(
    () => [
      {
        id: ALL_FILTER_ID,
        name: 'Todas las instituciones/organizaciones'
      },
      ...buildUniqueCodeOptions(
        scopedBeneficiaries
          .filter(beneficiary =>
            matchesBeneficiaryFilters(beneficiary, ['selectedSchoolName'])
          )
          .map(getBeneficiarySchoolName)
      )
    ],
    [scopedBeneficiaries, currentFilters, attendedSessionsByBeneficiary]
  );

  const mediatorOptions = useMemo(
    () =>
      buildMediatorOptions(
        scopedBeneficiaries.filter(beneficiary =>
          matchesBeneficiaryFilters(beneficiary, ['selectedMediator'])
        )
      ),
    [scopedBeneficiaries, currentFilters, attendedSessionsByBeneficiary]
  );

  const mediatorFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los mediadores' }, ...mediatorOptions],
    [mediatorOptions]
  );

  const selectedMediatorLabel = useMemo(() => getMultiFilterLabel(mediatorFilterOptions, selectedMediatorIds), [mediatorFilterOptions, selectedMediatorIds]);

  const groupFilterOptions = useMemo(
    () => [
      {
        id: ALL_FILTER_ID,
        name: isCopScope ? 'Todas las asesorías' : 'Todos los grupos'
      },
      ...buildUniqueCodeOptions(
        scopedBeneficiaries
          .filter(beneficiary =>
            matchesBeneficiaryFilters(beneficiary, ['selectedGroupCode'])
          )
          .map(beneficiary =>
            isCopScope
              ? getBeneficiaryAdvisorName(beneficiary)
              : getBeneficiaryGroupCode(beneficiary)
          )
      )
    ],
    [scopedBeneficiaries, currentFilters, isCopScope, attendedSessionsByBeneficiary]
  );

  const municipalityFilterOptions = useMemo(
    () => [
      { id: ALL_FILTER_ID, name: 'Todas las comunas' },
      ...buildLocationOptions(
        scopedBeneficiaries.filter(beneficiary =>
          matchesBeneficiaryFilters(beneficiary, ['selectedMunicipalityId'])
        ),
        getBeneficiaryMunicipalityId,
        getBeneficiaryMunicipalityName
      )
    ],
    [scopedBeneficiaries, currentFilters, attendedSessionsByBeneficiary]
  );

  const neighborhoodFilterOptions = useMemo(
    () => [
      { id: ALL_FILTER_ID, name: 'Todos los barrios' },
      ...buildLocationOptions(
        scopedBeneficiaries.filter(beneficiary =>
          matchesBeneficiaryFilters(beneficiary, ['selectedNeighborhoodId'])
        ),
        getBeneficiaryNeighborhoodId,
        getBeneficiaryNeighborhoodName
      )
    ],
    [scopedBeneficiaries, currentFilters, attendedSessionsByBeneficiary]
  );

  const selectedMunicipalityLabel = getMultiFilterLabel(municipalityFilterOptions, selectedMunicipalityIds);

  const selectedNeighborhoodLabel = getMultiFilterLabel(neighborhoodFilterOptions, selectedNeighborhoodIds);

  const selectedAttendedSessionLabel = getMultiFilterLabel(ATTENDED_SESSION_FILTER_OPTIONS, selectedAttendedSessionCounts);
  const selectedSchoolCodeLabel = getMultiFilterLabel(schoolFilterOptions, selectedSchoolCodes);
  const selectedSchoolNameLabel = getMultiFilterLabel(schoolNameFilterOptions, selectedSchoolNames);
  const selectedGroupCodeLabel = getMultiFilterLabel(groupFilterOptions, selectedGroupCodes);
  const selectedEntityTypeLabel = selectedEntityTypes.map(getEntityTypeFilterLabel).filter(Boolean).join(', ');

  const filteredBeneficiaries = scopedBeneficiaries.filter(beneficiary =>
    matchesBeneficiaryFilters(beneficiary)
  );

  const downloadableScopedBeneficiaries = useMemo(
    () =>
      scopedBeneficiaries.filter(beneficiary =>
        shouldIncludeBeneficiaryInCharacterizationAdminView(
          beneficiary,
          adminScope
        )
      ),
    [scopedBeneficiaries, adminScope]
  );

  const downloadableFilteredBeneficiaries = useMemo(
    () =>
      filteredBeneficiaries.filter(beneficiary =>
        shouldIncludeBeneficiaryInCharacterizationAdminView(
          beneficiary,
          adminScope
        )
      ),
    [filteredBeneficiaries, adminScope]
  );

  const hasActiveFilters = Boolean(
    searchName ||
      searchDocument ||
      ['pf', 'diplom', 'ndn'].includes(adminScope) ||
      selectedMediatorIds.length > 0 || selectedSchoolCodes.length > 0 ||
      (!isCopScope && selectedSchoolNames.length > 0) || selectedEntityTypes.length > 0 ||
      (!isCopScope && selectedGroupCodes.length > 0) || selectedMunicipalityIds.length > 0 ||
      selectedNeighborhoodIds.length > 0 || selectedAttendedSessionCounts.length > 0 ||
      dateFrom ||
      dateTo
  );

  const characterizationFiltersSearch = useMemo(
    () =>
      buildCharacterizationFiltersSearch({
        scope: adminScope,
        searchName,
        searchDocument,
        mediatorId: serializeMultiFilterValue(selectedMediatorIds),
        schoolCode: isCopScope ? '' : serializeMultiFilterValue(selectedSchoolCodes),
        schoolName: isCopScope ? '' : serializeMultiFilterValue(selectedSchoolNames),
        groupCode: isCopScope ? '' : serializeMultiFilterValue(selectedGroupCodes),
        advisorName: isCopScope ? serializeMultiFilterValue(selectedSchoolCodes) : '',
        municipalityId: serializeMultiFilterValue(selectedMunicipalityIds),
        neighborhoodId: serializeMultiFilterValue(selectedNeighborhoodIds),
        attendedSessions: serializeMultiFilterValue(selectedAttendedSessionCounts),
        dateFrom,
        dateTo
      }),
    [
      adminScope,
      searchName,
      searchDocument,
      selectedMediatorIds, selectedSchoolCodes, selectedSchoolNames, selectedGroupCodes,
      isCopScope,
      selectedMunicipalityIds, selectedNeighborhoodIds, selectedAttendedSessionCounts,
      dateFrom,
      dateTo
    ]
  );

  const characterizationBackTo = useMemo(
    () =>
      `${characterizationBasePath}${
        characterizationFiltersSearch ? `?${characterizationFiltersSearch}` : ''
      }`,
    [characterizationBasePath, characterizationFiltersSearch]
  );

  function handleMediatorFilter(_, values) { setSelectedMediatorIds(normalizeMultiSelection(values)); }
  function handleSchoolFilter(_, values) { setSelectedSchoolCodes(normalizeMultiSelection(values)); }
  function handleSchoolNameFilter(_, values) { setSelectedSchoolNames(normalizeMultiSelection(values)); }
  function handleEntityTypeFilter(_, values) { setSelectedEntityTypes(normalizeMultiSelection(values)); }
  function handleGroupFilter(_, values) { setSelectedGroupCodes(normalizeMultiSelection(values)); }
  function handleMunicipalityFilter(_, values) { setSelectedMunicipalityIds(normalizeMultiSelection(values)); setSelectedNeighborhoodIds([]); }
  function handleNeighborhoodFilter(_, values) { setSelectedNeighborhoodIds(normalizeMultiSelection(values)); }
  function handleAttendedSessionFilter(_, values) { setSelectedAttendedSessionCounts(normalizeMultiSelection(values)); }

  function handleClearFilters() {
    setSearchName('');
    setSearchDocument('');
    setSelectedMediatorIds([]); setSelectedSchoolCodes([]); setSelectedSchoolNames([]);
    setSelectedEntityTypes([]); setSelectedGroupCodes([]); setSelectedMunicipalityIds([]);
    setSelectedNeighborhoodIds([]); setSelectedAttendedSessionCounts([]);
    setDateFrom('');
    setDateTo('');
  }

  function handleDateFilterChange(key, value) {
    if (key === 'dateFrom') setDateFrom(value);
    if (key === 'dateTo') setDateTo(value);
  }

  async function handleDownloadConsents() {
    if (isDownloadingConsents) return;

    setIsDownloadingConsents(true);

    try {
      const search = new URLSearchParams();
      if (adminScope) {
        search.set('scope', adminScope);
      }

      const archiveUrl = `${getApiV1BaseUrl()}/beneficiary/consents-archive${
        search.toString() ? `?${search.toString()}` : ''
      }`;
      const response = await fetchWithAuth(archiveUrl, { method: 'GET' });

      if (!response.ok) {
        let errorCode = '';

        try {
          const errorPayload = await response.json();
          errorCode = String(errorPayload?.message || '').trim();
        } catch (_error) {}

        if (
          response.status === 404 ||
          errorCode === 'NO_CONSENT_FILES_FOUND'
        ) {
          toast('No hay consentimientos para descargar', {
            type: 'warning'
          });
          return;
        }

        throw new Error(errorCode || 'CONSENT_ARCHIVE_DOWNLOAD_FAILED');
      }

      const archiveBlob = await response.blob();
      const fileName = resolveDownloadFileName(
        response.headers.get('content-disposition'),
        `consentimientos_${getCurrentIsoDateInBogota()}.zip`
      );

      downloadBlob(archiveBlob, fileName);
      toast('Descarga de consentimientos iniciada', {
        type: 'success'
      });
    } catch (error) {
      console.error(error);
      toast('No se pudieron descargar los consentimientos', {
        type: 'error'
      });
    } finally {
      setIsDownloadingConsents(false);
    }
  }

  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (currentSearch === characterizationFiltersSearch) return;

    const nextSearchParams = new URLSearchParams(characterizationFiltersSearch);
    setSearchParams(nextSearchParams, { replace: true });
  }, [characterizationFiltersSearch, searchParams, setSearchParams]);

  return (
    <section className='characterizationAdmin-main-container'>
      <BreadCrumb />

      <div className='filters-container'>
        <div className='filters-top'>
          <p className='title'>FILTROS DE BUSQUEDA DE BENEFICIARIOS</p>
          <div className='filters-top-actions'>
            <button
              type='button'
              className='filters-clear-button'
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Limpiar filtros
            </button>
            <button
              type='button'
              className='filters-action-button'
              onClick={handleDownloadConsents}
              disabled={isDownloadingConsents}
            >
              {isDownloadingConsents
                ? 'Descargando...'
                : 'Descargar consentimientos'}
            </button>
            <DownloadBeneficiaries
              beneficiaries={downloadableScopedBeneficiaries}
              genders={genders}
              buttonText='Descargar beneficiarios'
              buttonIcon='download'
              fileName='beneficiarios.xlsx'
              containerClassName='characterization-download'
              buttonClassName='download-btn filters-action-button'
              characterizationMode={characterizationDownloadMode}
              disabled={downloadableScopedBeneficiaries.length === 0}
            />
          </div>
        </div>

        <div className='filters-content'>
          <AdminProcessScopeFilter onScopeChange={handleClearFilters} />
          <label className='label' htmlFor='beneficiary-name-filter'>
            <input
              id='beneficiary-name-filter'
              placeholder='Nombre completo'
              type='text'
              className='input-form'
              value={searchName}
              onChange={event => setSearchName(event.target.value)}
            />
          </label>

          <label className='label' htmlFor='beneficiary-document-filter'>
            <input
              id='beneficiary-document-filter'
              placeholder='Documento de identidad'
              type='text'
              inputMode='numeric'
              className='input-form'
              value={searchDocument}
              onChange={event => setSearchDocument(event.target.value)}
            />
          </label>

          <CustomFilterSelect
            optionsList={mediatorFilterOptions}
            placeholder={'Mediador'}
            addInfo={handleMediatorFilter}
            valueEdit={selectedMediatorLabel}
            multiple
            selectedValues={selectedMediatorIds}
            searchable={true}
            searchPlaceholder={'Buscar mediador'}
          />

          <CustomFilterSelect
            optionsList={schoolFilterOptions}
            placeholder={isCopScope ? 'Nombre Asesoría' : 'Codigo de Proceso Formativo'}
            addInfo={handleSchoolFilter}
            valueEdit={selectedSchoolCodeLabel}
            multiple
            selectedValues={selectedSchoolCodes}
            searchable={true}
            searchPlaceholder={isCopScope ? 'Buscar asesoría' : 'Buscar codigo'}
          />

          {!isCopScope && (
            <CustomFilterSelect
              optionsList={schoolNameFilterOptions}
              placeholder={'Institucion/organizacion'}
              addInfo={handleSchoolNameFilter}
              valueEdit={selectedSchoolNameLabel}
              multiple
              selectedValues={selectedSchoolNames}
              searchable={true}
              searchPlaceholder={'Buscar institucion u organizacion'}
            />
          )}

          {!isCopScope && (
            <CustomFilterSelect
              optionsList={ENTITY_TYPE_FILTER_OPTIONS}
              placeholder='Tipo de entidad'
              addInfo={handleEntityTypeFilter}
              valueEdit={selectedEntityTypeLabel}
              multiple
              selectedValues={selectedEntityTypes}
            />
          )}

          {!isCopScope && (
            <CustomFilterSelect
              optionsList={groupFilterOptions}
              placeholder='Grupo'
              addInfo={handleGroupFilter}
              valueEdit={selectedGroupCodeLabel}
              multiple
              selectedValues={selectedGroupCodes}
              searchable={true}
              searchPlaceholder='Buscar grupo'
            />
          )}

          <CustomFilterSelect
            optionsList={municipalityFilterOptions}
            placeholder={'Comuna'}
            addInfo={handleMunicipalityFilter}
            valueEdit={selectedMunicipalityLabel}
            multiple
            selectedValues={selectedMunicipalityIds}
            searchable={true}
            searchPlaceholder={'Buscar comuna'}
          />

          <CustomFilterSelect
            key={`characterization-neighborhood-${selectedMunicipalityIds.join('-') || 'all'}`}
            optionsList={neighborhoodFilterOptions}
            placeholder={'Barrio'}
            addInfo={handleNeighborhoodFilter}
            valueEdit={selectedNeighborhoodLabel}
            multiple
            selectedValues={selectedNeighborhoodIds}
            searchable={true}
            searchPlaceholder={'Buscar barrio'}
          />

          <CustomFilterSelect
            optionsList={ATTENDED_SESSION_FILTER_OPTIONS}
            placeholder={'Sesiones asistidas'}
            addInfo={handleAttendedSessionFilter}
            valueEdit={selectedAttendedSessionLabel}
            multiple
            selectedValues={selectedAttendedSessionCounts}
            searchable={false}
          />

          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={handleDateFilterChange}
            onClear={() => {
              setDateFrom('');
              setDateTo('');
            }}
          />
        </div>
      </div>

      <div className='beneficiaries-main-container'>
        <div className='beneficiaries-top'>
          <p className='title'>BENEFICIARIOS</p>
          <div className='beneficiaries-top-actions'>
            <p className='paginator-text'>
              Mostrando <span>{filteredBeneficiaries.length}</span> de{' '}
              <span>{scopedBeneficiaries.length}</span> resultados
            </p>
            <DownloadBeneficiaries
              beneficiaries={downloadableFilteredBeneficiaries}
              genders={genders}
              fileName='beneficiarios_filtrados.xlsx'
              containerClassName='beneficiaries-download'
              characterizationMode={characterizationDownloadMode}
              disabled={downloadableFilteredBeneficiaries.length === 0}
            />
          </div>
        </div>

        <div className='info-beneficiaries-content'>
          {filteredBeneficiaries.length > 0 ? (
            <BeneficiaryItem
              itemsDescription={itemsDescription}
              beneficiaries={filteredBeneficiaries}
              backTo={characterizationBackTo}
              adminScope={adminScope}
              detailPath={beneficiaryDetailPath}
            />
          ) : (
            <p className='beneficiaries-state'>
              {isLoadingAttendanceCounts && selectedAttendedSessionCounts.length > 0
                ? 'Cargando conteo de asistencias...'
                : hasActiveFilters
                ? 'No hay beneficiarios que coincidan con los filtros.'
                : 'Aun no hay beneficiarios para mostrar.'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default CharacterizationAdmin;
